import { AppLayout } from "@/components/AppLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Separator } from "@/components/ui/separator";
import { useSubscription } from "@/hooks/useSubscription";
import { useCredits } from "@/hooks/useCredits";
import { useToast } from "@/hooks/use-toast";
import { Skeleton } from "@/components/ui/skeleton";
import { useNavigate } from "react-router-dom";
import { 
  Crown, 
  Sparkles, 
  Zap, 
  Check, 
  Calendar, 
  CreditCard, 
  AlertCircle,
  ArrowRight,
  Shield,
  Infinity
} from "lucide-react";
import { format } from "date-fns";
import { fr } from "date-fns/locale";

const formatPrice = (price: number) => {
  return new Intl.NumberFormat('fr-FR').format(price);
};

const plans = [
  {
    id: "free",
    name: "Gratuit",
    price: 0,
    period: "",
    icon: Zap,
    description: "Pour découvrir Floop",
    features: [
      "10 crédits/mois",
      "Résolution HD",
      "Export PNG & JPG",
    ],
    limitations: [],
    buttonText: "Plan actuel",
    popular: false,
  },
  {
    id: "starter",
    name: "Starter",
    price: 5900,
    period: "/mois",
    icon: Sparkles,
    description: "Pour les créateurs",
    features: [
      "50 crédits/mois",
      "Floop Pro inclus",
      "Résolution 4K",
      "Support WhatsApp",
    ],
    limitations: [],
    buttonText: "Passer à Starter",
    popular: false,
  },
  {
    id: "pro",
    name: "Pro",
    price: 14900,
    period: "/mois",
    icon: Crown,
    description: "Pour les pros",
    features: [
      "250 crédits/mois",
      "Qualité 4K native",
      "Accès API",
      "Support prioritaire",
    ],
    limitations: [],
    buttonText: "Passer à Pro",
    popular: true,
  },
  {
    id: "business",
    name: "Studio Max",
    price: 47900,
    period: "/mois",
    icon: Crown,
    description: "Pour les équipes",
    features: [
      "500 crédits/mois",
      "Multi-utilisateurs",
      "API prioritaire",
      "Account manager",
    ],
    limitations: [],
    buttonText: "Passer à Studio Max",
    popular: false,
  },
];

export default function Subscription() {
  const { subscription, plan, isLoading, currentPlanFeatures, daysUntilExpiration, cancelAtPeriodEnd } = useSubscription();
  const { balance, creditsLoading } = useCredits();
  const { toast } = useToast();
  const navigate = useNavigate();

  const handleUpgrade = (planId: string) => {
    navigate("/payment");
  };

  const handleCancel = () => {
    toast({
      title: "Annulation",
      description: "Votre abonnement sera annulé à la fin de la période en cours.",
    });
    // TODO: Integrate Stripe cancellation
  };

  const handleReactivate = () => {
    toast({
      title: "Réactivation",
      description: "Votre abonnement a été réactivé.",
    });
    // TODO: Integrate Stripe reactivation
  };

  if (isLoading) {
    return (
      <AppLayout>
        <div className="p-6 space-y-6">
          <Skeleton className="h-8 w-64" />
          <div className="grid gap-6 md:grid-cols-2">
            <Skeleton className="h-48" />
            <Skeleton className="h-48" />
          </div>
        </div>
      </AppLayout>
    );
  }

  const currentPlan = plans.find(p => p.id === plan) || plans[0];
  const CurrentIcon = currentPlan.icon;

  return (
    <AppLayout>
      <div className="p-4 md:p-6 max-w-6xl mx-auto space-y-6 md:space-y-8">
        {/* Header */}
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-foreground">Mon abonnement</h1>
          <p className="text-sm md:text-base text-muted-foreground mt-1">
            Gérez votre plan et vos options
          </p>
        </div>

        {/* Current Plan Overview */}
        <div className="grid gap-4 md:gap-6 md:grid-cols-2">
          <Card className="bg-gradient-to-br from-card to-secondary/30 border-primary/20">
            <CardHeader>
              <CardTitle className="flex items-center gap-3">
                <div className={`p-2 rounded-xl ${
                  plan === "business" ? "bg-highlight/20" : 
                  plan === "pro" ? "bg-primary/20" : "bg-muted"
                }`}>
                  <CurrentIcon className={`w-6 h-6 ${
                    plan === "business" ? "text-highlight" : 
                    plan === "pro" ? "text-primary" : "text-muted-foreground"
                  }`} />
                </div>
                <div>
                  <span className="text-foreground">Plan {currentPlan.name}</span>
                  {plan !== "free" && (
                    <Badge variant="outline" className="ml-2 text-xs">
                      Actif
                    </Badge>
                  )}
                </div>
              </CardTitle>
              <CardDescription>
                {currentPlan.description}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {plan !== "free" && subscription?.current_period_end && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Calendar className="w-4 h-4" />
                  <span>
                    {cancelAtPeriodEnd ? "Expire le" : "Prochain renouvellement:"}{" "}
                    {format(new Date(subscription.current_period_end), "d MMMM yyyy", { locale: fr })}
                  </span>
                </div>
              )}

              {cancelAtPeriodEnd && (
                <div className="flex items-center gap-2 p-3 bg-destructive/10 rounded-xl text-sm">
                  <AlertCircle className="w-4 h-4 text-destructive" />
                  <span className="text-destructive">
                    Votre abonnement sera annulé dans {daysUntilExpiration} jours
                  </span>
                </div>
              )}

              <Separator />

              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Crédits mensuels</span>
                  <span className="font-medium text-foreground">{currentPlanFeatures.monthlyCredits}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Générations/jour</span>
                  <span className="font-medium text-foreground">
                    {currentPlanFeatures.maxGenerationsPerDay === -1 ? (
                      <Infinity className="w-4 h-4 inline" />
                    ) : currentPlanFeatures.maxGenerationsPerDay}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Résolution max</span>
                  <span className="font-medium text-foreground">{currentPlanFeatures.maxResolution}</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Credits Usage */}
          <Card className="bg-card border-border">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-foreground">
                <CreditCard className="w-5 h-5 text-primary" />
                Utilisation des crédits
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {creditsLoading ? (
                <Skeleton className="h-20" />
              ) : (
                <>
                  <div className="text-center">
                    <span className="text-4xl font-bold text-foreground">{balance}</span>
                    <span className="text-muted-foreground"> / {currentPlanFeatures.monthlyCredits}</span>
                  </div>
                  <Progress 
                    value={(balance / currentPlanFeatures.monthlyCredits) * 100} 
                    className="h-3"
                  />
                  <p className="text-sm text-muted-foreground text-center">
                    Crédits restants ce mois
                  </p>
                </>
              )}

              <Separator />

              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  className="flex-1 rounded-xl"
                  onClick={() => window.location.href = "/credits"}
                >
                  Acheter des crédits
                </Button>
                <Button 
                  variant="outline" 
                  className="flex-1 rounded-xl"
                  onClick={() => window.location.href = "/history"}
                >
                  Voir l'historique
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Plan Actions */}
        {plan !== "free" && (
          <Card className="bg-card border-border">
            <CardHeader>
              <CardTitle className="text-foreground">Actions</CardTitle>
            </CardHeader>
            <CardContent className="flex gap-4">
              {cancelAtPeriodEnd ? (
                <Button onClick={handleReactivate} className="bg-gradient-floop hover:opacity-90">
                  Réactiver l'abonnement
                </Button>
              ) : (
                <Button 
                  variant="outline" 
                  onClick={handleCancel}
                  className="text-destructive hover:text-destructive hover:bg-destructive/10"
                >
                  Annuler l'abonnement
                </Button>
              )}
              <Button variant="outline" onClick={() => window.location.href = "/billing"}>
                Gérer la facturation
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Available Plans */}
        <div>
          <h2 className="text-xl md:text-2xl font-bold text-foreground mb-4 md:mb-6">
            {plan === "free" ? "Choisir un plan" : "Changer de plan"}
          </h2>
          
          <div className="grid gap-4 md:gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {plans.map((planOption) => {
              const Icon = planOption.icon;
              const isCurrent = planOption.id === plan;
              const isUpgrade = plans.findIndex(p => p.id === planOption.id) > plans.findIndex(p => p.id === plan);
              
              return (
                <Card 
                  key={planOption.id}
                  className={`relative bg-card border-border transition-all duration-300 hover:border-primary/50 ${
                    planOption.popular ? "ring-2 ring-primary" : ""
                  } ${isCurrent ? "border-primary bg-primary/5" : ""}`}
                >
                  {planOption.popular && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                      <Badge className="bg-gradient-floop text-white border-0">
                        Plus populaire
                      </Badge>
                    </div>
                  )}
                  
                  <CardHeader className="text-center pb-2">
                    <div className={`mx-auto p-3 rounded-2xl w-fit ${
                      planOption.id === "business" ? "bg-highlight/20" : 
                      planOption.id === "pro" ? "bg-primary/20" : "bg-muted"
                    }`}>
                      <Icon className={`w-8 h-8 ${
                        planOption.id === "business" ? "text-highlight" : 
                        planOption.id === "pro" ? "text-primary" : "text-muted-foreground"
                      }`} />
                    </div>
                    <CardTitle className="text-xl text-foreground">{planOption.name}</CardTitle>
                    <div className="mt-2">
                      <span className="text-3xl font-bold text-foreground">
                        {planOption.price === 0 ? "Gratuit" : `${formatPrice(planOption.price)} FCFA`}
                      </span>
                      {planOption.period && (
                        <span className="text-muted-foreground">{planOption.period}</span>
                      )}
                    </div>
                    <CardDescription>{planOption.description}</CardDescription>
                  </CardHeader>
                  
                  <CardContent className="space-y-4">
                    <ul className="space-y-2">
                      {planOption.features.map((feature, i) => (
                        <li key={i} className="flex items-center gap-2 text-sm">
                          <Check className="w-4 h-4 text-success shrink-0" />
                          <span className="text-foreground">{feature}</span>
                        </li>
                      ))}
                    </ul>
                    
                    {isCurrent ? (
                      <Button disabled className="w-full rounded-xl">
                        <Shield className="w-4 h-4 mr-2" />
                        Plan actuel
                      </Button>
                    ) : isUpgrade ? (
                      <Button 
                        className="w-full rounded-xl bg-gradient-floop hover:opacity-90"
                        onClick={() => handleUpgrade(planOption.id)}
                      >
                        Passer à {planOption.name}
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </Button>
                    ) : (
                      <Button 
                        variant="outline" 
                        className="w-full rounded-xl"
                        onClick={() => handleUpgrade(planOption.id)}
                      >
                        Rétrograder
                      </Button>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>

        {/* FAQ */}
        <Card className="bg-card border-border">
          <CardHeader>
            <CardTitle className="text-foreground">Questions fréquentes</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <h4 className="font-medium text-foreground">Puis-je changer de plan à tout moment ?</h4>
              <p className="text-sm text-muted-foreground mt-1">
                Oui, vous pouvez upgrader ou downgrader votre plan à tout moment. Les changements prennent effet immédiatement.
              </p>
            </div>
            <Separator />
            <div>
              <h4 className="font-medium text-foreground">Que se passe-t-il si j'annule ?</h4>
              <p className="text-sm text-muted-foreground mt-1">
                Votre abonnement reste actif jusqu'à la fin de la période de facturation. Vous conservez l'accès à toutes les fonctionnalités.
              </p>
            </div>
            <Separator />
            <div>
              <h4 className="font-medium text-foreground">Les crédits non utilisés sont-ils reportés ?</h4>
              <p className="text-sm text-muted-foreground mt-1">
                Non, les crédits mensuels expirent à la fin de chaque période de facturation. Les crédits achetés séparément n'expirent jamais.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </AppLayout>
  );
}
